import { useState, useEffect, useCallback } from 'react';

// Generic hook for API calls with loading, error, and data states
export const useApi = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction();
      setData(result);
    } catch (err) {
      setError(err.message || 'An error occurred');
      console.error('API Hook Error:', err);
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};

// Hook for API mutations (create, update, delete)
export const useApiMutation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (apiFunction, ...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction(...args);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
  }, []);

  return { mutate, loading, error, reset };
};

// Hook for paginated API calls
export const usePaginatedApi = (apiFunction, pageSize = 10) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction({ page: pageNum, limit: pageSize });
      const newData = Array.isArray(result) ? result : result.data || [];
      
      if (reset || pageNum === 1) {
        setData(newData);
      } else {
        setData(prev => [...prev, ...newData]);
      }
      
      setHasMore(newData.length === pageSize);
      setPage(pageNum);
    } catch (err) {
      setError(err.message || 'An error occurred');
      console.error('Paginated API Hook Error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFunction, pageSize]);

  useEffect(() => {
    fetchData(1, true);
  }, [fetchData]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchData(page + 1, false);
    }
  }, [fetchData, loading, hasMore, page]);

  const refresh = useCallback(() => {
    fetchData(1, true);
  }, [fetchData]);

  return { data, loading, error, hasMore, loadMore, refresh };
};

// Hook for real-time data updates
export const useRealTimeApi = (apiFunction, intervalMs = 30000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await apiFunction();
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'An error occurred');
      console.error('Real-time API Hook Error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFunction]);

  useEffect(() => {
    fetchData();
    
    const interval = setInterval(fetchData, intervalMs);
    return () => clearInterval(interval);
  }, [fetchData, intervalMs]);

  const forceRefresh = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  return { data, loading, error, lastUpdated, forceRefresh };
};

// Hook for search functionality
export const useApiSearch = (searchFunction, debounceMs = 300) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.trim() === '') {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const result = await searchFunction(searchQuery);
        setResults(Array.isArray(result) ? result : []);
      } catch (err) {
        setError(err.message || 'Search failed');
        console.error('Search API Hook Error:', err);
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [searchFunction, debounceMs]
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return { query, setQuery, results, loading, error, clearSearch };
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Hook for form submissions with API calls
export const useApiForm = (submitFunction) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(async (formData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const result = await submitFunction(formData);
      setSuccess(true);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Form submission failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [submitFunction]);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
    setLoading(false);
  }, []);

  return { submit, loading, error, success, reset };
};

export default useApi;